// =============================================
// Step1DomainPicker — krok 1 kreatora: wybor domen.
// Toolbar: workspace (Sponsoraki tylko z flaga), szukajka, filtry "bez CF" /
// "z hostingiem", "Pobierz zony" (OBOWIAZKOWE przed "Dalej"), "Sprawdz NS
// zaznaczonych" (sekwencyjnie), "Zaznacz wszystkie przefiltrowane",
// "+ Dodaj domene spoza listy". Tabela: ☐ · domena · wygasa · AfterMarket ·
// obecne NS · ma zone · IP hostingu.
// =============================================
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, RefreshCw, Search, Radar, CheckSquare, Plus, AlertTriangle, X } from "lucide-react";
import type { CloudflareAccount } from "@/hooks/useCloudflareAccounts";
import type { AllZonesState } from "@/hooks/useAllZones";
import type { TransferDomainsState } from "@/hooks/useTransferDomains";
import type { TransferWizardState } from "@/hooks/useTransferWizard";
import { WORKSPACE_LABEL, type AmWorkspace } from "@/services/aftermarketService";
import { isCloudflareNs } from "@/services/cloudflare/transferPlanBuilder";
import { StatusBadge } from "@/components/cloudflare/shared/StatusBadge";

interface Props {
  accounts: CloudflareAccount[];
  allZones: AllZonesState;
  transferDomains: TransferDomainsState;
  wizard: TransferWizardState;
}

export const Step1DomainPicker = ({ accounts, allZones, transferDomains, wizard }: Props) => {
  const { workspace, setWorkspace, canSideprojects, effectiveWorkspace, loading } = transferDomains;
  const { candidates, selectedKeys, toggle, selectMany, clearSelection, addManualDomain, nsCache, nsCheck, checkNs, cancelNsCheck } = wizard;
  const [search, setSearch] = useState("");
  const [onlyNoCf, setOnlyNoCf] = useState(false);
  const [onlyWithHosting, setOnlyWithHosting] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [manualError, setManualError] = useState<string | null>(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return candidates.filter((d) => {
      if (q && !d.key.includes(q)) return false;
      const cache = nsCache[d.key];
      const cf = cache?.status === "ok" && isCloudflareNs(cache.ns);
      const zone = allZones.findZone(d.key);
      if (onlyNoCf && (cf || zone)) return false;
      if (onlyWithHosting && !d.hostingIp && !d.candidateIps?.length) return false;
      return true;
    });
  }, [candidates, search, onlyNoCf, onlyWithHosting, nsCache, allZones]);

  const allVisibleSelected = visible.length > 0 && visible.every((d) => selectedKeys.has(d.key));
  const selectedList = [...selectedKeys];

  const handleAddManual = () => {
    const err = addManualDomain(manualInput);
    setManualError(err);
    if (!err) setManualInput("");
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Konto AfterMarket / workspace</Label>
          <Select value={effectiveWorkspace} onValueChange={(v) => setWorkspace(v as AmWorkspace)}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="firma">🏢 Firma</SelectItem>
              {canSideprojects && <SelectItem value="sideprojects">🎨 Sponsoraki</SelectItem>}
            </SelectContent>
          </Select>
        </div>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Szukaj domeny…" className="pl-8 w-56" />
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer"><Checkbox checked={onlyNoCf} onCheckedChange={(v) => setOnlyNoCf(!!v)} />bez CF</label>
        <label className="flex items-center gap-2 text-xs cursor-pointer"><Checkbox checked={onlyWithHosting} onCheckedChange={(v) => setOnlyWithHosting(!!v)} />z hostingiem</label>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void allZones.refresh()} disabled={allZones.loading || !accounts.length}>
            {allZones.loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {allZones.loadedAt ? `Odśwież zony (${allZones.zones.length})` : "Pobierz zony"}
          </Button>
          {nsCheck.running ? (
            <Button variant="outline" size="sm" onClick={cancelNsCheck}><X className="h-4 w-4 mr-2" />Przerwij ({nsCheck.done}/{nsCheck.total})</Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => void checkNs(selectedList)} disabled={!selectedList.length} title="Pobiera obecne NS z AfterMarket, domena po domenie">
              <Radar className="h-4 w-4 mr-2" />Sprawdź NS zaznaczonych ({selectedList.length})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => (allVisibleSelected ? selectMany(visible.map((d) => d.key), false) : selectMany(visible.map((d) => d.key), true))} disabled={!visible.length}>
            <CheckSquare className="h-4 w-4 mr-2" />{allVisibleSelected ? "Odznacz przefiltrowane" : `Zaznacz przefiltrowane (${visible.length})`}
          </Button>
        </div>
      </div>

      {nsCheck.running && <Progress value={(nsCheck.done / Math.max(1, nsCheck.total)) * 100} className="h-1.5" />}

      {/* Zony — obowiazkowe */}
      {!allZones.loadedAt && !allZones.loading && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Najpierw pobierz zony z kont Cloudflare</AlertTitle>
          <AlertDescription className="text-xs">Bez listy zon kreator nie wie, które domeny już mają zonę — „Dalej” jest zablokowane.</AlertDescription>
        </Alert>
      )}
      {allZones.failures.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Zony z {allZones.failures.length} {allZones.failures.length === 1 ? "konta" : "kont"} nie pobrane</AlertTitle>
          <AlertDescription className="text-xs">
            {allZones.failures.map((f) => <div key={f.cfAccountId}><strong>{f.label}</strong>: {f.error}</div>)}
            <Button variant="outline" size="sm" className="mt-2 h-7" onClick={() => void allZones.refresh()}>Spróbuj ponownie</Button>
          </AlertDescription>
        </Alert>
      )}

      {/* + domena spoza listy */}
      <div className="flex items-center gap-2">
        <Input value={manualInput} onChange={(e) => { setManualInput(e.target.value); setManualError(null); }} onKeyDown={(e) => { if (e.key === "Enter") handleAddManual(); }} placeholder="+ domena spoza panelu (np. przyklad.pl)" className="w-72 font-mono text-sm" />
        <Button variant="outline" size="sm" onClick={handleAddManual} disabled={!manualInput.trim()}><Plus className="h-4 w-4 mr-1" />Dodaj</Button>
        {manualError && <span className="text-xs text-red-400">{manualError}</span>}
        <span className="text-xs text-muted-foreground ml-auto">
          zaznaczone: <strong>{selectedKeys.size}</strong> · konto {WORKSPACE_LABEL[effectiveWorkspace]} · {candidates.length} domen
          {selectedKeys.size > 0 && <Button variant="ghost" size="sm" className="h-6 ml-2 px-2" onClick={clearSelection}>wyczyść</Button>}
        </span>
      </div>

      {/* Tabela */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="rounded-lg border overflow-x-auto max-h-[60vh] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="w-8"><Checkbox checked={allVisibleSelected} onCheckedChange={(v) => selectMany(visible.map((d) => d.key), !!v)} /></TableHead>
                <TableHead>Domena</TableHead>
                <TableHead>Wygasa</TableHead>
                <TableHead>AfterMarket</TableHead>
                <TableHead>Obecne NS</TableHead>
                <TableHead>Zona w CF</TableHead>
                <TableHead>IP hostingu</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Brak domen pasujących do filtrów.</TableCell></TableRow>
              ) : visible.map((d) => {
                const cache = nsCache[d.key];
                const zone = allZones.findZone(d.key);
                const cf = cache?.status === "ok" && isCloudflareNs(cache.ns);
                return (
                  <TableRow key={d.key} data-state={selectedKeys.has(d.key) ? "selected" : undefined} className="cursor-pointer" onClick={() => toggle(d.key)}>
                    <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={selectedKeys.has(d.key)} onCheckedChange={() => toggle(d.key)} /></TableCell>
                    <TableCell className="font-mono text-sm">
                      {d.key}
                      {d.manual && <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0">spoza panelu</Badge>}
                      {d.workspace === "sideprojects" && <span className="ml-1.5 text-xs" title="Sponsoraki">🎨</span>}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">{d.expiry ?? "—"}</TableCell>
                    <TableCell className="text-xs">
                      {d.expirySource === "not_on_account"
                        ? <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500/40 text-amber-400">brak na AfterMarket</Badge>
                        : d.expirySource === "aftermarket" ? <span className="text-muted-foreground">na koncie</span>
                          : <span className="text-muted-foreground">?</span>}
                    </TableCell>
                    <TableCell className="text-xs max-w-[260px]">
                      {!cache ? <span className="text-muted-foreground">—</span>
                        : cache.status === "error" ? <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-red-500/40 text-red-400" title={cache.error}>błąd NS</Badge>
                          : cf ? <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-emerald-500/40 text-emerald-400" title={cache.ns.join(", ")}>już Cloudflare</Badge>
                            : <span className="font-mono text-muted-foreground truncate block" title={cache.ns.join("\n")}>{cache.ns.length ? cache.ns.join(", ") : "brak NS"}</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {zone ? <span className="inline-flex items-center gap-1.5"><StatusBadge status={zone.status} className="text-[10px] px-1.5 py-0" /><span className="text-muted-foreground">{zone.cfAccountLabel}</span></span>
                        : allZones.loadedAt ? <span className="text-muted-foreground">brak</span> : <span className="text-muted-foreground">?</span>}
                    </TableCell>
                    <TableCell className="text-xs font-mono">
                      {d.hostingIp ?? (d.ipAmbiguous ? <span className="text-amber-400" title={d.candidateIps?.join(", ")}>kilka IP</span> : <span className="text-muted-foreground">—</span>)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
